import { useEffect, useId, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode, type Ref } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CalendarClock, Check, Ellipsis, Globe, Heart, Link2, Lock, MessageCircle, Pencil, Pin, PinOff, Play, Trash2, X } from "lucide-react";
import { CHELSEA, CLASSES, POSTS, PROJECTS, countComments, hasReplay, type Comment, type Member, type Post, type Role } from "./sample";
import { Locked, LockedRow, M, VIS_LABEL, VisBadge, joinCta, useRole, withRole, type LockWhat, type Need } from "./Members";
import { EASE } from "../bits";
import "./Posts.css";

/* Shared pieces for the club's posts feed (Posts.tsx) and a single post
   (Post.tsx): a tiny in-memory store so likes, votes, comments and Chelsea's
   edits carry between the two pages, the post card, poll, media, locked
   state, and a toast. Nothing here talks to a server; it's all sample data. */

export type Vis = Post["visibility"];
export type Kind = Post["kind"];
export type LiveComment = Omit<Comment, "replies"> & { liked?: boolean; hidden?: boolean; reported?: boolean; mine?: boolean; replies?: LiveComment[] };
export type LivePost = Omit<Post, "comments"> & {
  comments: LiveComment[]; liked?: boolean; vote?: number; edited?: boolean;
  /** Set while a post is scheduled; only Chelsea sees it until it goes out. */
  scheduled?: string;
  /** Alt text for photos Chelsea attached in the composer. */
  alts?: Record<string, string>;
};

/* ─── Store ─── */

let posts: LivePost[] = POSTS.map(p => ({ ...p, comments: p.comments as LiveComment[] }));
const subs = new Set<() => void>();
const commit = (next: LivePost[]) => { posts = next; subs.forEach(f => f()); };
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f); }; };

export const usePosts = () => useSyncExternalStore(subscribe, () => posts);

export const postStore = {
  patch(id: string, fn: (p: LivePost) => LivePost) { commit(posts.map(p => (p.id === id ? fn(p) : p))); },
  add(p: LivePost) { commit([p, ...posts]); },
  /** Removes a post and hands back an undo. */
  remove(id: string) {
    const i = posts.findIndex(p => p.id === id), gone = posts[i];
    commit(posts.filter(p => p.id !== id));
    return () => { if (!gone || posts.some(p => p.id === id)) return; const next = [...posts]; next.splice(i, 0, gone); commit(next); };
  },
  /** One pinned post at a time, like Patreon. */
  pin(id: string) { const on = !posts.find(p => p.id === id)?.pinned; commit(posts.map(p => ({ ...p, pinned: p.id === id ? on : false }))); },
};

/** Published posts in feed order: the pinned one first, then newest first. */
export const published = (list: LivePost[]) => {
  const live = list.filter(p => !p.scheduled);
  return [...live.filter(p => p.pinned), ...live.filter(p => !p.pinned)];
};

export const toggleLike = <T extends { liked?: boolean; likes: number }>(x: T): T => ({ ...x, liked: !x.liked, likes: x.likes + (x.liked ? -1 : 1) });

/** Change (or with null, delete) one comment anywhere in a thread. */
export function mapComment(list: LiveComment[], id: string, fn: (c: LiveComment) => LiveComment | null): LiveComment[] {
  return list.flatMap(c => {
    if (c.id === id) { const n = fn(c); return n ? [n] : []; }
    return [c.replies ? { ...c, replies: mapComment(c.replies, id, fn) } : c];
  });
}

/* ─── Toast (module-level too, so a delete on the post page can be undone on the feed) ─── */

type Toast = { id: number; text: string; action?: { label: string; run: () => void }; link?: { to: string; label: string } };
let toastNow: Toast | null = null;
const toastSubs = new Set<() => void>();
let toastTimer: number | undefined;
export function toast(t: Omit<Toast, "id">) {
  toastNow = { ...t, id: Date.now() };
  toastSubs.forEach(f => f());
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { toastNow = null; toastSubs.forEach(f => f()); }, t.action ? 7000 : 4200);
}
const dismiss = () => { window.clearTimeout(toastTimer); toastNow = null; toastSubs.forEach(f => f()); };

export function ToastHost() {
  const t = useSyncExternalStore(f => { toastSubs.add(f); return () => { toastSubs.delete(f); }; }, () => toastNow);
  return <div className="mp-toast-host" role="status" aria-live="polite">
    <AnimatePresence>
      {t && <motion.div key={t.id} className="mp-toast" initial={{ opacity: 0, y: 16, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.3, ease: EASE }}>
        <span>{t.text}</span>
        {t.action && <button type="button" onClick={() => { t.action!.run(); dismiss(); }}>{t.action.label}</button>}
        {t.link && <Link to={t.link.to} onClick={dismiss}>{t.link.label}</Link>}
        <button type="button" className="mp-toast-x" onClick={dismiss} aria-label="Dismiss"><X size={15} aria-hidden="true" /></button>
      </motion.div>}
    </AnimatePresence>
  </div>;
}

/* ─── Who's looking ─── */

/** Who can open a post: public for all, Thread Friend posts from $3, member posts for members. */
export const canSee = (role: Role, vis: Vis) => vis === "public" || role === "member" || role === "chelsea" || (vis === "friends" && role === "friend");

/** Which tier a post needs before it opens. */
export const needFor = (vis: Vis): Need => (vis === "friends" ? "friends" : "members");

/** Internal links keep the review bar's role (and any #hash). */
export function useHref() {
  const role = useRole();
  return (path: string, hash = "") => withRole(`${path}${hash}`, role);
}

/** Visitors are nudged to join instead of liking, voting or commenting. */
export function useJoinNudge() {
  const role = useRole();
  return (what: string) => {
    const c = joinCta(role);
    if (c) toast({ text: `Thread Friends and Members can ${what}.`, link: { to: c.to, label: c.short } });
  };
}

/* ─── Small parts ─── */

export const KIND: Record<Kind, string> = { project: "This month’s project", replay: "Class replay", poll: "Poll", photo: "Photo", update: "Update" };
/** Icons for the visibility picker (labels come from the shared VIS_LABEL, same as VisBadge). */
const VIS_ICON: Record<Vis, typeof Globe> = { public: Globe, friends: Heart, members: Lock };

const AV_TONES = ["soft", "pop", "extra", "mint"];
export function Avatar({ who, size = 40 }: { who: Member; size?: number }) {
  if (who.avatar) return <img className="mp-av" src={who.avatar} alt="" width={size} height={size} style={{ width: size, height: size }} />;
  const tone = AV_TONES[[...who.id].reduce((n, c) => n + c.charCodeAt(0), 0) % AV_TONES.length];
  const initials = who.name.split(/\s+/).map(w => w[0]).join("").slice(0, 2);
  return <span className={`mp-av mp-av-${tone}`} style={{ width: size, height: size, fontSize: size * 0.38 }} aria-hidden="true">{initials}</span>;
}

/** Chelsea's name line, with her creator badge. */
export function Byline({ post }: { post: LivePost }) {
  return <div className="mp-by">
    <Avatar who={CHELSEA} size={42} />
    <p>
      <b>Chelsea</b> <span className="mp-creator">creator</span>
      <span className="mp-meta">{post.scheduled ? "Scheduled" : post.ago}{post.edited && " · edited"}</span>
    </p>
  </div>;
}

export function LikeButton({ liked, count, onToggle, what = "post", small = false }: { liked?: boolean; count: number; onToggle: () => void; what?: string; small?: boolean }) {
  const role = useRole();
  const nudge = useJoinNudge();
  const click = () => (role === "visitor" ? nudge("like posts and comments") : onToggle());
  return <button type="button" className={`mp-like${liked ? " is-on" : ""}${small ? " mp-like-sm" : ""}`} aria-pressed={!!liked} onClick={click} aria-label={`Like this ${what}`}>
    <motion.span className="mp-like-ic" key={liked ? "on" : "off"} initial={liked ? { scale: 0.6 } : false} animate={{ scale: [null, 1.3, 1] }} transition={{ duration: 0.35, ease: EASE }}>
      <Heart size={small ? 15 : 19} fill={liked ? "currentColor" : "none"} aria-hidden="true" />
    </motion.span>
    <span className="mp-like-n">{count}<span className="mp-sr"> {count === 1 ? "like" : "likes"}</span></span>
  </button>;
}

export function ShareButton({ id }: { id: string }) {
  async function copy() {
    try { await navigator.clipboard.writeText(`${window.location.origin}${M.post(id)}`); } catch { /* clipboard blocked; the toast still confirms */ }
    toast({ text: "Link copied. Only people who can see this post can open it." });
  }
  return <button type="button" className="mp-act" onClick={copy}><Link2 size={17} aria-hidden="true" /> <span>Share</span></button>;
}

/* ─── Media per post kind ─── */

const ALT: Record<string, string> = {
  "/chelsea.JPG": "Chelsea in a patchwork top and gingham pants, holding a round quilted bag",
  "/chelsea-and-cosmo.jpg": "Chelsea smiling at her sewing machine with Cosmo the cat curled up on her lap",
  "/chelsea-rainbow-portrait.jpg": "Chelsea in front of a rainbow wall of fabric",
  "/concept/gingham-top.jpg": "A gingham quilted vest", "/img/products/qs-mat.jpg": "A stack of zipper pouches", "/concept/label.jpg": "A woven label sewn into a seam",
};
export const altFor = (post: LivePost, src: string) => post.alts?.[src] ?? ALT[src] ?? post.title;

function Polaroid({ src, alt, tilt = -2, tape = "pop", note }: { src: string; alt: string; tilt?: number; tape?: string; note?: string }) {
  return <figure className="mp-pola" style={{ rotate: `${tilt}deg` }}>
    <i className={`mp-tape mp-tape-${tape}`} aria-hidden="true" />
    <img src={src} alt={alt} loading="lazy" />
    {note && <figcaption>{note}</figcaption>}
  </figure>;
}

function ProjectMedia({ post, full }: { post: LivePost; full: boolean }) {
  const href = useHref();
  const project = PROJECTS.find(p => p.id === post.projectId) ?? PROJECTS[0];
  const classes = CLASSES.filter(c => c.projectId === project.id);
  const img = post.images?.[0] ?? project.image;
  return <div className={`mp-project${full ? " is-full" : ""}`}>
    <Polaroid src={img} alt={altFor(post, img)} tilt={-2.5} note={`the ${project.title.toLowerCase()}!`} />
    <div className="mp-project-card">
      <p className="mp-project-month">{project.month}</p>
      <p className="mp-project-title">{project.title}</p>
      <p className="mp-project-skill">{project.skill}</p>
      {classes.length > 0 && <ul className="mp-project-dates">
        {classes.map(c => <li key={c.id}><b>{c.part.replace(/ of \d+/, "")}</b><span>{c.date.replace(/, \d{4}$/, "")} · 7 PM MT</span></li>)}
      </ul>}
      {classes[0] && <Link className="mb-btn mb-btn-ink mb-btn-sm mp-project-go" to={href(M.klass(classes[0].id))}>Supplies, pattern & classes <ArrowRight size={15} aria-hidden="true" /></Link>}
    </div>
  </div>;
}

function ReplayMedia({ post }: { post: LivePost }) {
  const href = useHref();
  const k = CLASSES.find(c => c.id === post.classId);
  if (!k) return null;
  // Tonight's (or a future) class has no replay yet, so it never links to one.
  if (!hasReplay(k)) return <div className="mp-replay is-soon">
    <span className="mp-replay-img"><img src={k.poster} alt="" loading="lazy" /><em className="mp-replay-len"><CalendarClock size={13} aria-hidden="true" /> Airs {k.date.replace(/, \d{4}$/, "")} · 7 PM MT</em></span>
    <span className="mp-replay-meta"><small>{k.part} · {k.date}</small><strong>{k.title}</strong><span className="mp-watched">The replay goes up after the live class.</span></span>
  </div>;
  const pct = Math.round((k.watched ?? 0) * 100);
  return <Link className="mp-replay" to={href(M.klass(k.id))} aria-label={`Watch the replay: ${k.title}, ${k.part}, ${k.length}`}>
    <span className="mp-replay-img">
      <img src={k.poster} alt="" loading="lazy" />
      <b className="mp-play"><Play size={22} fill="currentColor" aria-hidden="true" /></b>
      <em className="mp-replay-len">{k.length}</em>
      <span className="mp-replay-chaps">{k.chapters.length} chapters</span>
    </span>
    <span className="mp-replay-meta">
      <small>{k.part} · {k.date}</small>
      <strong>{k.title}</strong>
      {pct > 0 && pct < 100 && <span className="mp-progress"><span style={{ width: `${pct}%` }} /><em>{pct}% watched · pick up where you left off</em></span>}
      {pct >= 100 && <span className="mp-watched"><Check size={13} aria-hidden="true" /> Watched</span>}
    </span>
  </Link>;
}

/** A poll you vote on right in the card; results show once you've voted (Chelsea always sees them). */
export function Poll({ post }: { post: LivePost }) {
  const role = useRole();
  const nudge = useJoinNudge();
  const id = useId();
  const poll = post.poll!;
  const total = poll.options.reduce((n, o) => n + o.votes, 0);
  const voted = post.vote !== undefined;
  const results = voted || role === "chelsea";
  const lead = total ? poll.options.reduce((best, o, i) => (o.votes > poll.options[best].votes ? i : best), 0) : -1;
  const cast = (i: number | undefined) => postStore.patch(post.id, p => ({
    ...p, vote: i,
    poll: { ...p.poll!, options: p.poll!.options.map((o, j) => ({ ...o, votes: o.votes + (j === i ? 1 : 0) - (j === p.vote ? 1 : 0) })) },
  }));
  return <div className="mp-poll" role="group" aria-labelledby={id}>
    <p id={id} className={poll.q === post.title ? "mp-sr" : "mp-poll-q"}><span aria-hidden="true">✎</span> {poll.q}</p>
    {results
      ? <ul className="mp-poll-res">
        {poll.options.map((o, i) => {
          const pct = total ? Math.round((o.votes / total) * 100) : 0;
          return <li key={o.label} className={`${i === post.vote ? "is-mine" : ""}${i === lead ? " is-lead" : ""}`}>
            <motion.span className="mp-poll-bar" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }} aria-hidden="true" />
            <span className="mp-poll-label">{i === post.vote && <Check size={15} aria-label="Your vote" />}{o.label}</span>
            <b>{role === "chelsea" && <small className="mp-poll-n">{o.votes} {o.votes === 1 ? "vote" : "votes"}</small>}{pct}%{role !== "chelsea" && <span className="mp-sr">, {o.votes} votes</span>}</b>
          </li>;
        })}
      </ul>
      : <div className="mp-poll-opts">
        {poll.options.map((o, i) => <button key={o.label} type="button" onClick={() => (role === "visitor" ? nudge("vote in polls") : cast(i))}><i aria-hidden="true" />{o.label}</button>)}
      </div>}
    <p className="mp-poll-foot" aria-live="polite">
      <span>{total} votes</span>
      {voted ? <><span aria-hidden="true">·</span><span>Thanks for voting!</span><button type="button" onClick={() => cast(undefined)}>Change my vote</button></>
        : role === "chelsea" ? <><span aria-hidden="true">·</span><span>You see results as they come in</span></>
          : <><span aria-hidden="true">·</span><span>Vote to see results</span></>}
    </p>
  </div>;
}

function Photos({ post }: { post: LivePost }) {
  const imgs = post.images ?? [];
  return <div className={`mp-photos mp-photos-${Math.min(imgs.length, 3)}`}>
    {imgs.map((src, i) => <Polaroid key={src + i} src={src} alt={altFor(post, src)} tilt={[1.8, -2.2, 1.2][i % 3]} tape={["soft", "extra", "pop"][i % 3]} />)}
  </div>;
}

/** The kind-specific part of a post (images, replay, poll). */
export function Media({ post, full = false }: { post: LivePost; full?: boolean }) {
  switch (post.kind) {
    case "project": return <ProjectMedia post={post} full={full} />;
    case "replay": return <ReplayMedia post={post} />;
    case "poll": return post.poll ? <Poll post={post} /> : null;
    default: return post.images?.length ? <Photos post={post} /> : null;
  }
}

/* ─── Locked ─── */

/** What someone who can't open a post sees: the club's shared Locked card over a
    blurred hint of the post, or (compact) the shared one-line LockedRow. */
export function PostLocked({ post, big = false, compact = false }: { post: LivePost; big?: boolean; compact?: boolean }) {
  const need = needFor(post.visibility);
  if (compact) return <LockedRow need={need} className="mp-lockrow" />;
  const img = post.kind === "replay" ? CLASSES.find(c => c.id === post.classId)?.poster : post.images?.[0];
  const what: LockWhat = post.kind === "replay" ? "replays" : "posts";
  const hint = post.kind === "poll" ? <span className="mp-locked-bars"><i /><i /><i /></span> : <span className="mp-locked-lines"><i /><i /><i /><i /></span>;
  return <Locked need={need} what={what} className={`mp-locked${big ? " is-big" : ""}`} {...(img ? { image: img } : { peek: hint })} />;
}

/* ─── Chelsea's tools ─── */

/** Pin / edit / delete, as a small disclosure menu on each post. */
export function PostMenu({ post, onEdit, onDeleted }: { post: LivePost; onEdit: () => void; onDeleted?: () => void }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); btn.current?.focus(); } };
    document.addEventListener("pointerdown", down);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("pointerdown", down); document.removeEventListener("keydown", key); };
  }, [open]);
  const run = (f: () => void) => () => { setOpen(false); f(); };
  const del = () => {
    const undo = postStore.remove(post.id);
    onDeleted?.();
    toast({ text: `Deleted “${post.title}”.`, action: { label: "Undo", run: undo } });
  };
  return <div className="mp-menu" ref={wrap}>
    <button ref={btn} type="button" className="mp-icon-btn" aria-expanded={open} aria-controls={menuId} aria-label="Manage this post" onClick={() => setOpen(o => !o)}>
      <Ellipsis size={20} aria-hidden="true" />
    </button>
    <AnimatePresence>
      {open && <motion.ul id={menuId} className="mp-menu-list" initial={{ opacity: 0, y: -6, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.18 }}>
        <li><button type="button" onClick={run(() => { postStore.pin(post.id); toast({ text: post.pinned ? "Unpinned." : "Pinned to the top of the feed." }); })}>
          {post.pinned ? <PinOff size={16} aria-hidden="true" /> : <Pin size={16} aria-hidden="true" />} {post.pinned ? "Unpin" : "Pin to top"}</button></li>
        <li><button type="button" onClick={run(onEdit)}><Pencil size={16} aria-hidden="true" /> Edit post</button></li>
        <li><button type="button" className="is-danger" onClick={run(del)}><Trash2 size={16} aria-hidden="true" /> Delete</button></li>
      </motion.ul>}
    </AnimatePresence>
  </div>;
}

/** Visibility as a row of radio chips, shared by the composer and the edit form. */
export function VisPicker({ value, onChange, name }: { value: Vis; onChange: (v: Vis) => void; name: string }) {
  return <fieldset className="mp-pick">
    <legend>Who can see it</legend>
    <div className="mp-pick-row">
      {(Object.keys(VIS_LABEL) as Vis[]).map(v => { const { label, who } = VIS_LABEL[v]; const Icon = VIS_ICON[v];
        return <label key={v} className={`mp-pick-chip${value === v ? " is-on" : ""}`}>
          <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} />
          <Icon size={14} aria-hidden="true" /> {label}<span className="mp-sr"> ({who})</span>
        </label>; })}
    </div>
    <p className="mp-pick-help">{VIS_LABEL[value].who}</p>
  </fieldset>;
}

export function EditForm({ post, onDone }: { post: LivePost; onDone: () => void }) {
  const [title, setTitle] = useState(post.title);
  const [body, setBody] = useState(post.body);
  const [vis, setVis] = useState<Vis>(post.visibility);
  const id = useId();
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    postStore.patch(post.id, p => ({ ...p, title: title.trim(), body: body.trim(), visibility: vis, edited: true }));
    toast({ text: "Saved. Everyone sees the new version." });
    onDone();
  };
  return <form className="mp-edit" onSubmit={save} onKeyDown={e => { if (e.key === "Escape") onDone(); }}>
    <p className="mp-edit-note">Editing</p>
    <label className="mp-field"><span>Title</span><input ref={first} value={title} onChange={e => setTitle(e.target.value)} required /></label>
    <label className="mp-field"><span>Post</span><textarea value={body} rows={4} onChange={e => setBody(e.target.value)} /></label>
    <VisPicker value={vis} onChange={setVis} name={`${id}-vis`} />
    <div className="mp-edit-acts">
      <button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={onDone}>Cancel</button>
      <button type="submit" className="mb-btn mb-btn-ink mb-btn-sm" disabled={!title.trim()}>Save changes</button>
    </div>
  </form>;
}

/* ─── The card ─── */

/** Takes a ref (React 19 passes it as a prop) so AnimatePresence's popLayout can measure a card as it leaves. */
export function PostCard({ post, index = 0, compactLock = false, ref }: { post: LivePost; index?: number; compactLock?: boolean; ref?: Ref<HTMLElement> }) {
  const role = useRole();
  const href = useHref();
  const [editing, setEditing] = useState(false);
  const titleId = useId();
  const open = canSee(role, post.visibility);
  const comments = countComments(post, role === "chelsea");
  return <motion.article ref={ref} layout="position" className={`mp-card mp-kind-${post.kind}${post.pinned ? " is-pinned" : ""}${open ? "" : " is-locked"}`} aria-labelledby={titleId}
    initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.2 } }}
    transition={{ duration: 0.45, delay: Math.min(index, 5) * 0.06, ease: EASE }}>
    {post.pinned && <><i className="mp-tape mp-tape-pop mp-card-tape" aria-hidden="true" /><span className="mp-pinned"><Pin size={13} aria-hidden="true" /> pinned</span></>}
    <header className="mp-card-head">
      <Byline post={post} />
      <VisBadge visibility={post.visibility} />
      {role === "chelsea" && !editing && <PostMenu post={post} onEdit={() => setEditing(true)} />}
    </header>
    {editing ? <EditForm post={post} onDone={() => setEditing(false)} /> : <>
      <p className="mp-kind">{KIND[post.kind]}</p>
      <h3 id={titleId} className="mp-title">
        {open ? <Link to={href(M.post(post.id))}>{post.title}</Link> : <>{post.title}</>}
      </h3>
      {open ? <>
        <p className="mp-body">{post.body}</p>
        <Media post={post} />
      </> : <PostLocked post={post} compact={compactLock} />}
    </>}
    <footer className="mp-card-foot">
      {open ? <>
        <LikeButton liked={post.liked} count={post.likes} onToggle={() => postStore.patch(post.id, toggleLike)} />
        <Link className="mp-act" to={href(M.post(post.id), "#comments")}><MessageCircle size={17} aria-hidden="true" /> {comments}<span className="mp-sr"> {comments === 1 ? "comment" : "comments"}</span></Link>
        <ShareButton id={post.id} />
        <Link className="mp-open" to={href(M.post(post.id))} aria-label={`Open “${post.title}”`}>Open <ArrowRight size={15} aria-hidden="true" /></Link>
      </> : <p className="mp-quiet"><Heart size={15} aria-hidden="true" /> {post.likes} <span className="mp-sr">likes</span><span aria-hidden="true">·</span> <MessageCircle size={15} aria-hidden="true" /> {comments ? `${comments} ${comments === 1 ? "comment" : "comments"} from ${post.visibility === "members" ? "members" : "the club"}` : "No comments yet"}</p>}
    </footer>
  </motion.article>;
}

/** A small framed panel for sidebars. */
export function SideCard({ className = "", children, labelledBy }: { className?: string; children: ReactNode; labelledBy?: string }) {
  return <section className={`mp-side-card ${className}`} aria-labelledby={labelledBy}>{children}</section>;
}
